/**
 * Represents a secret source which can be either a default source or vault
 */
export interface SecretSource {
  sourceId: number;
  projectId: number;
  sourceType: SecretSourceType;
  name: string;
  config: SecretSourceConfig;
}

/**
 * Valid types of secret sources. Must match ehq_core's SecretSourceType enum
 * (serialized as the variant name). `GcpSecretManager` is only usable on a
 * GCP-hosted deployment — availability is gated by the deployment's
 * `secretSources` list (see DeploymentInfoService), not hardcoded here.
 */
export type SecretSourceType = 'Default' | 'Vault' | 'GcpSecretManager';

/**
 * Source types whose stored secret "value" is a pointer/reference (a Vault path,
 * or a GCP secret id / resource name) rather than the secret itself. Used to
 * label and validate the value field accordingly. NOTE: this does not affect
 * `gettable` — whether a step can read the value back is an admin choice made per
 * secret, independent of the source type.
 */
export const POINTER_SOURCE_TYPES: readonly SecretSourceType[] = ['Vault', 'GcpSecretManager'];

/**
 * Represents a secret with its metadata.
 *
 * The list (`GET .../entries`) and get (`GET .../entries/{id}`) endpoints return
 * a sanitized shape and NEVER include the value — the value is only available via
 * the dedicated `GET .../entries/{id}/value` endpoint (see SecretValueResponse).
 * `sourceType` and `sourceName` describe the backing secret source.
 */
export interface Secret {
  secretId: number;
  sourceId: number;
  projectId: number;
  gettable: boolean;
  name: string;
  sourceType: SecretSourceType;
  sourceName: string;
  /**
   * @deprecated The list/get endpoints no longer return the value. It is only
   * populated by the `/value` endpoint response. Retained as optional so legacy
   * consumers compile; prefer SecretsService.getSecretValue().
   */
  value?: string;
}

/**
 * Request payload for creating a new secret source
 */
export interface CreateSecretSourceRequest {
  name: string;
  sourceType: SecretSourceType;
  config: SecretSourceConfig;
}

/**
 * Request payload for updating an existing secret source
 */
export interface UpdateSecretSourceRequest {
  name?: string;
  config?: SecretSourceConfig;
}

/**
 * Request payload for creating a new secret
 */
export interface CreateSecretRequest {
  name: string;
  value: string;
  sourceId: number;
  gettable: boolean;
}

/**
 * Request payload for updating an existing secret
 */
export interface UpdateSecretRequest {
  name?: string;
  sourceId?: number;
  value?: string;
  gettable?: boolean;
}

/**
 * Response payload when retrieving a secret value.
 * The server returns the full Secret object; we only use the value field here.
 */
export interface SecretValueResponse {
  value?: string;
}

/**
 * Configuration for Default- and Vault-type secret sources: both are empty.
 * Vault's url + token are configured on the server (SCRUM-695), so the CLI/web
 * never send them; a Vault source is created with no config, like Default.
 */
export interface DefaultConfig {
  // Empty object for default and vault sources
}

/**
 * GCP Secret Manager configuration. Holds the GCP project that short secret ids
 * resolve against; the server reads the `project_id` key (snake_case) and
 * resolves values via ambient credentials, so no token is stored.
 */
export interface GcpSecretManagerConfig {
  project_id: string;
}

/**
 * Union type for secret source configurations
 */
export type SecretSourceConfig =
  | DefaultConfig
  | GcpSecretManagerConfig
  | Record<string, any>;

/**
 * Error response structure matching API documentation
 */
export interface SecretApiError {
  error: 'PermissionDenied' | 'NotFound' | 'UnknownError' | string;
  message: string;
}
